/**
 * Ticket Lifecycle v2 — Slice 5D-11 Batch 1
 *
 * Advisor-aware Bay Occupancy estimate. Pure, display-only helpers
 * used by `TicketCard` (Dayboard) to render:
 *
 *     Tech OptiTime: 1h 20m
 *     Advisor Bay Hold: +12m predicted
 *     Bay Occupancy: 1h 32m
 *     Bay Finish: 2:32 PM
 *
 * Conceptual distinction (load-bearing — pinned in the spec doc):
 *
 *   Tech OptiTime    = active technician/bay work estimate. Drives
 *                      Chronomancer, smart-assign, the timer, every
 *                      stored scheduling field. **NOT modified here.**
 *
 *   Advisor Bay Hold = the portion of advisor / FOH / approval time
 *                      that keeps the *vehicle physically in the bay*.
 *                      Not all advisor delay qualifies — see the
 *                      bay-presence gate below.
 *
 *   Bay Occupancy    = Tech OptiTime + Advisor Bay Hold.
 *                      The vehicle-in-bay wall-clock estimate.
 *
 *   Bay Finish       = startTime + Bay Occupancy. Display only.
 *
 * Scope: **display-only.** This module is forbidden from feeding
 * Chronomancer / globalCascade / reassignCore / scheduling writes /
 * bay overlap constraints / technician efficiency. Batch 2 will
 * introduce a deliberate, audited bridge between this estimate and
 * the scheduling write path. Until then the only consumer is the
 * `TicketCard` advisor-bay-occupancy block behind the
 * `advisorBayOccupancyOnDayboard` flag.
 *
 * Purity contract:
 *   - Same inputs always produce the same output.
 *   - No Date.now(), no new Date() (caller passes the wall clock).
 *   - Does not mutate inputs.
 *   - Negative inputs clamp to 0; output minutes are never negative.
 *
 * Why bay-presence is a hard gate
 * --------------------------------
 * Not all advisor delay blocks a bay. Examples from the slice spec:
 *
 *   1. Vehicle stays in bay while advisor sells the work
 *        → advisor delay DOES block the bay.
 *   2. Vehicle is moved to parking lot while waiting approval
 *        → advisor delay affects ticket lifecycle/promise time but
 *          does NOT block the bay.
 *   3. Drop-off waiting in parking lot before work starts
 *        → no bay impact yet.
 *
 * Batch 1 therefore refuses to count advisor minutes as bay hold
 * unless the helper believes the vehicle is currently bay-present.
 * `bayPresence: 'unknown'` is treated conservatively — the estimate
 * falls back to "may affect bay" wording and never adds advisor
 * minutes to Bay Occupancy.
 */

// ===================================================================
// Bay presence
// ===================================================================

export type VehicleBayPresence = 'in_bay' | 'in_parking_lot' | 'unknown';

export interface VehicleBayPresenceInput {
  /** `tickets.bay_id` — UUID when occupying a bay, null otherwise. */
  bayId: string | null | undefined;
  /** `tickets.parking_spot_id` — UUID when in a parking spot. */
  parkingSpotId: string | null | undefined;
  /** Optional explicit parking-lot signal (e.g. parent component
   *  already knows the ticket is in parking lot collection). */
  isInParkingLot?: boolean | null;
  /** Existing top-level `tickets.vehicle_present` derived field. */
  vehiclePresent?: boolean | null;
  /** Ticket status — Cancelled/Deleted are non-bay regardless of
   *  bay_id residue. */
  status?: string | null;
}

/**
 * Decide whether a ticket's vehicle is currently occupying a bay,
 * sitting in the parking lot, or unknown. Pure boolean composition
 * — no DB calls, no time math.
 *
 * Precedence (first matching rule wins):
 *   1. Terminal statuses (`Cancelled`, `Deleted`, `Completed`) →
 *      `in_parking_lot` if parking_spot_id, else `unknown`. We do
 *      NOT call these `in_bay` even if bay_id residue exists.
 *   2. Explicit parking-lot prop → `in_parking_lot`.
 *   3. `bayId` set AND not in parking lot AND vehiclePresent !== false
 *      → `in_bay`.
 *   4. `parkingSpotId` set → `in_parking_lot`.
 *   5. otherwise → `unknown`.
 */
export function detectVehicleBayPresence(
  input: VehicleBayPresenceInput,
): VehicleBayPresence {
  const status = (input.status ?? '').toString();
  const terminal = status === 'Cancelled' || status === 'Deleted' || status === 'Completed';
  const hasBay = !!(input.bayId && String(input.bayId).trim().length > 0);
  const hasSpot = !!(input.parkingSpotId && String(input.parkingSpotId).trim().length > 0);
  const explicitParkingLot = input.isInParkingLot === true;

  if (terminal) {
    if (hasSpot || explicitParkingLot) return 'in_parking_lot';
    return 'unknown';
  }
  if (explicitParkingLot) return 'in_parking_lot';
  if (hasBay && input.vehiclePresent !== false) return 'in_bay';
  if (hasSpot) return 'in_parking_lot';
  return 'unknown';
}

// ===================================================================
// Advisor Bay Hold
// ===================================================================

/**
 * Source of the advisor bay hold value. Drives the human-readable
 * suffix on the TicketCard ("observed" vs "predicted") and lets
 * Batch 2 distinguish observed vs predicted in scheduling rules.
 */
export type AdvisorBayHoldSource =
  | 'observed'
  | 'predicted'
  | 'none'
  | 'unavailable_bay_not_present'
  | 'unavailable_bay_presence_unknown'
  | 'unavailable_insufficient_history';

export interface AdvisorBayHoldInput {
  /**
   * Observed advisor delay impact for THIS ticket in minutes (e.g.
   * the value already produced by `calculateAdvisorDelayImpact`).
   * `null` means "no observed value yet".
   */
  observedAdvisorDelayMinutes: number | null | undefined;
  /**
   * Predicted advisor delay impact for THIS advisor × bucket in
   * minutes. `null` means "no usable prediction". Batch 1 will
   * usually receive `null` from `TicketCard` (no cheap per-card
   * prediction source); Batch 2 may inject board-aggregated values.
   */
  predictedAdvisorDelayMinutes: number | null | undefined;
  /** Whether the vehicle is currently in a bay. Required gate. */
  bayPresence: VehicleBayPresence;
  /**
   * Whether the vehicle is expected to REMAIN bay-present for the
   * duration of the advisor delay window. Today the only safe
   * answer for an `in_bay` ticket is `true`; Batch 2 may refine
   * this with an explicit "moves-to-parking-on-pause" flow.
   */
  vehicleExpectedToRemainInBay?: boolean;
}

export interface AdvisorBayHoldResult {
  /**
   * Minutes to add to Tech OptiTime for the Bay Occupancy
   * calculation. `null` when the helper refuses to attribute any
   * advisor minutes to the bay (parking lot, unknown presence,
   * insufficient history).
   */
  minutes: number | null;
  /** Which signal supplied the value (or why none could be used). */
  source: AdvisorBayHoldSource;
  /**
   * Short rendering hint. UI consumers should NOT mutate copy text
   * downstream — keep wording centralised so the drift detector
   * can pin label changes.
   *   - 'value'      → render `"+Xm observed"` or `"+Xm predicted"`
   *   - 'parking_lot'→ "Vehicle in parking lot — bay hold N/A"
   *   - 'unknown'    → "Bay hold may apply" (conservative)
   *   - 'history'    → "Not enough history yet"
   */
  reason:
    | 'value'
    | 'parking_lot'
    | 'unknown_bay_presence'
    | 'insufficient_history';
}

function clampNonNeg(n: number | null | undefined): number | null {
  if (n == null || !Number.isFinite(n)) return null;
  return n < 0 ? 0 : n;
}

/**
 * Decide how many advisor minutes count toward Bay Occupancy.
 *
 * Rules (first matching wins):
 *   1. `bayPresence === 'in_parking_lot'` → 0 minutes attributed;
 *      source='unavailable_bay_not_present'; reason='parking_lot'.
 *      Advisor delay still affects vehicle lifecycle / promise time
 *      but the bay is not held.
 *   2. `bayPresence === 'unknown'` → 0 minutes attributed;
 *      source='unavailable_bay_presence_unknown'; reason='unknown'.
 *      Conservative — refuse to attribute without a confident
 *      bay-present signal.
 *   3. Bay-present + observed value finite → minutes=observed;
 *      source='observed'; reason='value'.
 *   4. Bay-present + predicted value finite + advisor expected to
 *      remain in bay → minutes=predicted; source='predicted';
 *      reason='value'.
 *   5. Bay-present but no value → minutes=null;
 *      source='unavailable_insufficient_history';
 *      reason='insufficient_history'.
 */
export function calculateAdvisorBayHoldMinutes(
  input: AdvisorBayHoldInput,
): AdvisorBayHoldResult {
  if (input.bayPresence === 'in_parking_lot') {
    return {
      minutes: null,
      source: 'unavailable_bay_not_present',
      reason: 'parking_lot',
    };
  }
  if (input.bayPresence === 'unknown') {
    return {
      minutes: null,
      source: 'unavailable_bay_presence_unknown',
      reason: 'unknown_bay_presence',
    };
  }
  // in_bay branch
  const observed = clampNonNeg(input.observedAdvisorDelayMinutes);
  if (observed !== null) {
    return { minutes: observed, source: 'observed', reason: 'value' };
  }
  const predicted = clampNonNeg(input.predictedAdvisorDelayMinutes);
  if (predicted !== null && input.vehicleExpectedToRemainInBay !== false) {
    return { minutes: predicted, source: 'predicted', reason: 'value' };
  }
  return {
    minutes: null,
    source: 'unavailable_insufficient_history',
    reason: 'insufficient_history',
  };
}

// ===================================================================
// Bay Occupancy Display
// ===================================================================

export interface BayOccupancyEstimateInput {
  /** Tech wall-clock minutes already computed elsewhere
   *  (`calculateWallMinutes(predicted_minutes, ...)`). `null` ⇒ the
   *  card can't anchor Bay Occupancy at all. */
  techOptiTimeMinutes: number | null | undefined;
  /** Pre-computed advisor bay hold result from
   *  `calculateAdvisorBayHoldMinutes`. */
  advisorBayHold: AdvisorBayHoldResult;
  /**
   * Optional anchor wall time used to compute Bay Finish (`Date`).
   * Pass the same start instant the existing OptiFinish calc uses.
   * When null/undefined, `bayFinishUtc` is null.
   */
  startInstant?: Date | null;
}

export interface BayOccupancyEstimateResult {
  /** Tech OptiTime minutes echoed back, clamped non-negative. */
  techOptiTimeMinutes: number | null;
  /** Advisor bay hold minutes folded in, or null when unavailable. */
  advisorBayHoldMinutes: number | null;
  /** Total bay-occupied wall minutes (tech + bay-attributed advisor). */
  bayOccupancyMinutes: number | null;
  /**
   * `true` when the value is the lower bound (no advisor bay hold
   * value could be folded in). Display layer renders a leading `≥`.
   */
  lowerBound: boolean;
  /**
   * Bay Finish as an absolute UTC instant. `null` when no
   * `startInstant` was passed or `techOptiTimeMinutes` is missing.
   * Caller decides how to format in shop timezone — this helper
   * stays time-zone-agnostic to keep purity simple.
   */
  bayFinishUtc: Date | null;
  /** Echo of the advisor bay hold result so the UI can read source/reason. */
  advisorBayHold: AdvisorBayHoldResult;
  /**
   * Whether the Bay Occupancy block should render at all. False
   * when there's no Tech OptiTime to anchor — without the floor,
   * the block has nothing to say.
   */
  shouldRender: boolean;
}

/**
 * Format the advisor bay hold for display on the ticket card.
 *
 * Internally we keep `advisorBayHoldMinutes` as a precise number
 * (history-derived means may carry several decimal places — e.g.
 * 2.9726166666666667 from the raw average). For the on-card readout we
 * round to the nearest whole minute and render with a leading `+`, which
 * matches the surrounding "+N min" convention the codebase uses for
 * other delay readouts (TicketCard.tsx:1053, line 2129).
 *
 * Returns null when there's no concrete number to display, so the
 * caller can decide whether to skip the row entirely.
 *
 * Pure: same input ⇒ same output.
 */
export function formatAdvisorBayHoldMinutesForDisplay(
  minutes: number | null | undefined,
): string | null {
  if (minutes === null || minutes === undefined) return null;
  if (!Number.isFinite(minutes)) return null;
  return `+${Math.round(minutes)} min`;
}

export function calculateBayOccupancyEstimate(
  input: BayOccupancyEstimateInput,
): BayOccupancyEstimateResult {
  const tech = clampNonNeg(input.techOptiTimeMinutes);
  if (tech === null) {
    return {
      techOptiTimeMinutes: null,
      advisorBayHoldMinutes: input.advisorBayHold.minutes,
      bayOccupancyMinutes: null,
      lowerBound: false,
      bayFinishUtc: null,
      advisorBayHold: input.advisorBayHold,
      shouldRender: false,
    };
  }
  const bayHold = input.advisorBayHold.minutes;
  const bayOccupancyMinutes = bayHold === null ? tech : tech + bayHold;
  const lowerBound = bayHold === null;
  let bayFinishUtc: Date | null = null;
  if (input.startInstant && input.startInstant instanceof Date && !Number.isNaN(input.startInstant.getTime())) {
    bayFinishUtc = new Date(input.startInstant.getTime() + bayOccupancyMinutes * 60_000);
  }
  return {
    techOptiTimeMinutes: tech,
    advisorBayHoldMinutes: bayHold,
    bayOccupancyMinutes,
    lowerBound,
    bayFinishUtc,
    advisorBayHold: input.advisorBayHold,
    shouldRender: true,
  };
}
